import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import fieldCategoryService from '../../../../services/fieldCategoryService';
import authService from '../../../../services/auth';

/**
 * Field Category Management component
 * Allows administrators to:
 * - View all categories in the system
 * - Create new custom categories
 * - Edit existing custom categories
 * - Delete custom categories
 */
const FieldCategoryManagement = () => {
  // State for categories data
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for the new category form
  const [newCategory, setNewCategory] = useState({
    code: '',
    displayName: '',
    description: ''
  });
  
  // State for category edit form
  const [editCategory, setEditCategory] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  
  // State for feedback messages
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    category: null
  });

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);
  
  // Function to load categories from the API
  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the service to load categories, with fallback to public endpoint
      const sortedCategories = await fieldCategoryService.getCategories();
      
      // Sort categories by display order, then by display name
      setCategories(sortedCategories.sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) {
          return a.displayOrder - b.displayOrder;
        }
        return a.displayName.localeCompare(b.displayName);
      }));
    } catch (error) {
      console.error('Error loading field categories:', error);
      setError('Failed to load field categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to handle form input changes
  const handleInputChange = (e, form = 'new') => {
    const { name, value } = e.target;
    
    if (form === 'new') {
      setNewCategory({
        ...newCategory,
        [name]: value
      });
    } else {
      setEditCategory({
        ...editCategory,
        [name]: value
      });
    }
  };
  
  // Function to create a new category
  const createCategory = async (e) => {
    e.preventDefault();
    
    // Convert code to uppercase with underscores
    const code = newCategory.code.toUpperCase().replace(/\s+/g, '_');
    
    try {
      const createdCategory = await fieldCategoryService.createCategory({
        ...newCategory,
        code
      });
      
      // Add the new category to the list
      setCategories([...categories, createdCategory]);
      
      // Reset the form
      setNewCategory({
        code: '',
        displayName: '',
        description: ''
      });
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Category created successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating category:', error);
      
      // Show error message
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to create category',
        severity: 'error'
      });
    }
  };
  
  // Function to open the edit dialog
  const handleOpenEditDialog = (category) => {
    setEditCategory({ ...category });
    setOpenEditDialog(true);
  };
  
  // Function to close the edit dialog
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditCategory(null);
  };
  
  // Function to update a category
  const updateCategory = async () => {
    try {
      await fieldCategoryService.updateCategory(editCategory.code, editCategory);
      
      // Update the category in the list
      const updatedCategories = categories.map(cat => 
        cat.code === editCategory.code ? editCategory : cat
      );
      
      setCategories(updatedCategories);
      
      // Close the dialog
      handleCloseEditDialog();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Category updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating category:', error);
      
      // Show error message
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update category',
        severity: 'error'
      });
    }
  };
  
  // Function to open delete confirmation dialog
  const handleOpenDeleteDialog = (category) => {
    setDeleteConfirmation({
      open: true,
      category
    });
  };
  
  // Function to close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmation({
      open: false,
      category: null
    });
  };
  
  // Function to delete a category
  const deleteCategory = async () => {
    try {
      await fieldCategoryService.deleteCategory(deleteConfirmation.category.code);
      
      // Remove the category from the list
      const updatedCategories = categories.filter(cat => 
        cat.code !== deleteConfirmation.category.code
      );
      
      setCategories(updatedCategories);
      
      // Close the dialog
      handleCloseDeleteDialog();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Category deleted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      
      // Show error message
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete category',
        severity: 'error'
      });
      
      // Close the dialog
      handleCloseDeleteDialog();
    }
  };
  
  // Function to close the snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Field Categories
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage field categories used to organize fields in workflows, tickets, and services.
        System categories cannot be modified, but you can create custom categories for your organization.
      </Typography>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* List of categories */}
      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    No categories found.
                  </TableCell>
                </TableRow>
              ) : (
                categories.map(category => (
                  <TableRow key={category.code}>
                    <TableCell>{category.code}</TableCell>
                    <TableCell>{category.displayName}</TableCell>
                    <TableCell>
                      {category.description ? (
                        category.description
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          No description
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {category.isSystem ? (
                        <Chip
                          label="System"
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="Custom"
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {category.isSystem ? (
                        <Tooltip title="System categories cannot be modified">
                          <span>
                            <IconButton disabled>
                              <EditIcon />
                            </IconButton>
                            <IconButton disabled>
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <>
                          <Tooltip title="Edit category">
                            <IconButton onClick={() => handleOpenEditDialog(category)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete category">
                            <IconButton onClick={() => handleOpenDeleteDialog(category)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Create new category form */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Create New Category
        </Typography>
        <form onSubmit={createCategory}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code"
                name="code"
                value={newCategory.code}
                onChange={handleInputChange}
                fullWidth
                required
                helperText="Unique identifier, will be converted to uppercase with underscores"
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Display Name"
                name="displayName"
                value={newCategory.displayName}
                onChange={handleInputChange}
                fullWidth
                required
                helperText="Human-readable name shown in the UI"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Description"
                name="description"
                value={newCategory.description}
                onChange={handleInputChange}
                fullWidth
                helperText="Optional description of this category"
                inputProps={{ maxLength: 255 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
              >
                Create Category
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* Edit category dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code"
                value={editCategory?.code || ''}
                fullWidth
                disabled
                helperText="Code cannot be changed"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Display Name"
                name="displayName"
                value={editCategory?.displayName || ''}
                onChange={(e) => handleInputChange(e, 'edit')}
                fullWidth
                required
                helperText="Human-readable name shown in the UI"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Description"
                name="description"
                value={editCategory?.description || ''}
                onChange={(e) => handleInputChange(e, 'edit')}
                fullWidth
                helperText="Optional description of this category"
                inputProps={{ maxLength: 255 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={updateCategory} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmation.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category "{deleteConfirmation.category?.displayName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={deleteCategory} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      
      {/* Feedback snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FieldCategoryManagement;
